import React, { ReactNode, useRef, useState } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { setPreventUnload } from 'flow/actions/app-actions';
import { attachmentImageLoad } from 'flow/actions/chat-actions';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { Attachment, Message, NewAttachment } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { getAttachmentLink, getAttachmentPreviewURL } from 'flow/utils';
import {
    copyImage,
    getAttachmentTypeFromExtension,
    getImageFromChatAttachment,
} from 'flow/utils/message-utils';
import { twMerge } from 'tailwind-merge';
import PreviewImageWithRetry from './PreviewImageWithRetry';

interface Props {
    message: TypedMap<Message>;
    sessionKey: ChatSessionKey;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const MessageAttachment = ({
    message,
    sessionKey,
    videoDark = false,
}: Props) => {
    const attachment = message.get('attachment');
    if (!attachment) {
        return null;
    }
    return attachment.get('previewData') || attachment.get('previewUrl') ? (
        <MessageAttachmentPreview
            message={message}
            attachment={attachment}
            sessionKey={sessionKey}
            videoDark={videoDark}
        />
    ) : (
        <MessageAttachmentFile
            attachment={attachment}
            sessionKey={sessionKey}
            videoDark={videoDark}
        />
    );
};

interface MessageAttachmentFileProps {
    attachment: TypedMap<Attachment> & TypedMap<NewAttachment>;
    sessionKey: ChatSessionKey;
    videoDark?: boolean;
}

const MessageAttachmentFile = ({
    attachment,
    sessionKey,
    videoDark = false,
}: MessageAttachmentFileProps) => {
    const fileName = attachment.get('fileName') || attachment.get('name');
    const fileType = getAttachmentTypeFromExtension(fileName);
    const icon = getImageFromChatAttachment(fileType as any);

    const fileData = (
        <>
            <Icon
                iconKey={icon}
                className={videoDark ? 'text-gray-300' : 'text-gray-600'}
            />
            <span
                className={twMerge(
                    'text-sm wrap-anywhere',
                    videoDark ? 'text-gray-200' : 'text-gray-800',
                )}
            >
                {fileName}
            </span>
        </>
    );

    return (
        <div
            className={twMerge(
                'flex w-auto max-w-full min-w-2 items-center gap-x-2 rounded-sm border p-2 @sm/messages:min-w-3',
                videoDark ? 'border-gray-600' : 'border-gray-300',
            )}
        >
            {attachment.has('uuid') ? (
                <WithToolbar
                    attachment={attachment}
                    sessionKey={sessionKey}
                    videoDark={videoDark}
                >
                    <a
                        href={getAttachmentLink(sessionKey, attachment)}
                        download={true}
                        onClick={() => setPreventUnload(true)}
                    >
                        {fileData}
                    </a>
                </WithToolbar>
            ) : (
                fileData
            )}
        </div>
    );
};

interface MessageAttachmentPreviewProps {
    message: TypedMap<Message>;
    attachment: TypedMap<Attachment> & TypedMap<NewAttachment>;
    sessionKey: ChatSessionKey;
    videoDark?: boolean;
}

const MessageAttachmentPreview = ({
    message,
    attachment,
    sessionKey,
    videoDark = false,
}: MessageAttachmentPreviewProps) => {
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const [isPreviewError, setIsPreviewError] = useState(false);

    const downloadLink = getAttachmentLink(sessionKey, attachment);
    const isGif = attachment.get('fileName').toLowerCase().endsWith('gif');
    const imageRef = useRef<HTMLImageElement | null>(null);
    const rawHeight = attachment.get('previewHeight');
    const rawWidth = attachment.get('previewWidth');
    const previewHeight = Math.min(rawHeight, 200);
    const previewWidth =
        rawHeight > 0
            ? Math.round(rawWidth * (previewHeight / rawHeight))
            : undefined;

    const openImagePreview = () => {
        openModal('imagePreview', {
            messageKey: message.get('key'),
            sessionKey,
        });
    };

    const [upSrc] = useState(
        isGif ? downloadLink : getAttachmentPreviewURL(attachment),
    );

    return (
        <WithToolbar
            attachment={attachment}
            sessionKey={sessionKey}
            imageRef={imageRef}
            isGif={isGif}
            isPreviewError={isPreviewError}
            videoDark={videoDark}
        >
            <PreviewImageWithRetry
                attachment={attachment}
                ref={imageRef}
                src={upSrc}
                className="max-h-[200px] cursor-pointer rounded-sm"
                onLoad={() => {
                    dispatch(attachmentImageLoad());
                }}
                height={previewHeight}
                width={previewWidth}
                alt="Message Attachment Preview"
                onButtonClick={openImagePreview}
                onButtonKeyDown={(e) => {
                    if (e.code === 'Enter') {
                        openImagePreview();
                    }
                }}
                onDownloadButton={setIsPreviewError}
            />
        </WithToolbar>
    );
};

interface WithToolbarProps {
    attachment: TypedMap<Attachment>;
    sessionKey: ChatSessionKey;
    imageRef?: React.MutableRefObject<HTMLImageElement | null>;
    isGif?: boolean;
    children: ReactNode;
    isPreviewError?: boolean;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const WithToolbar = ({
    attachment,
    sessionKey,
    imageRef,
    isGif,
    children,
    isPreviewError,
    videoDark = false,
}: WithToolbarProps) => {
    const { i18next } = useAppContext();
    const [toolbarVisible, setToolbarVisible] = useState(false);
    const [playing, setPlaying] = useState(true);
    const downloadLink = getAttachmentLink(sessionKey, attachment);
    return (
        <div className="flex flex-row items-start">
            <div
                className="relative"
                onMouseEnter={() => setToolbarVisible(true)}
                onMouseLeave={() => setToolbarVisible(false)}
            >
                <div
                    className={twMerge(
                        'absolute z-10 flex flex-row gap-x-1 rounded-sm p-1 shadow-md',
                        videoDark ? 'bg-gray-700 text-gray-200' : 'bg-white',
                        toolbarVisible ? '' : 'hidden',
                        isPreviewError
                            ? '-end-10 -top-0.5'
                            : '-end-1.5 -top-1.5',
                    )}
                >
                    <a
                        href={downloadLink}
                        download={true}
                        onClick={() => {
                            setPreventUnload(true);
                        }}
                    >
                        <IconButton
                            tooltipLabel={i18next.t('Tooltip.Audio.Download')}
                        >
                            <Icon iconKey="download" />
                        </IconButton>
                    </a>
                    {!isGif && imageRef?.current && (
                        <IconButton
                            onClick={() => copyImage(downloadLink)}
                            tooltipLabel={i18next.t('Tooltip.Title.Copy')}
                        >
                            <Icon iconKey="copy" />
                        </IconButton>
                    )}
                    {isGif && playing && (
                        <IconButton
                            tooltipLabel={i18next.t('Tooltip.Title.Stop')}
                            onClick={() => {
                                setPlaying(false);
                                stopGifPlaying(imageRef);
                            }}
                        >
                            <Icon iconKey="stop" />
                        </IconButton>
                    )}
                    {isGif && !playing && (
                        <IconButton
                            onClick={() => {
                                setPlaying(true);
                                resumeGifPlaying(imageRef, downloadLink);
                            }}
                            tooltipLabel={i18next.t('Tooltip.Title.Start')}
                        >
                            <Icon iconKey="play_arrow" />
                        </IconButton>
                    )}
                </div>
                {children}
            </div>
        </div>
    );
};

const stopGifPlaying = (
    imageRef: React.MutableRefObject<HTMLImageElement | null>,
) => {
    // There isn't any standard browser API to pause an animated gif, however by now the
    // Canvas API (including toDataURL) is very broadly available at the time of writing
    // (2024),so we can write the image into a canvas to snapshot it.
    const image = imageRef.current;
    if (!image) {
        return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    canvas.getContext('2d').drawImage(image, 0, 0, image.width, image.height);
    image.src = canvas.toDataURL('image/gif');
};

const resumeGifPlaying = (
    imageRef: React.MutableRefObject<HTMLImageElement | null>,
    originalUrl: string,
) => {
    // Restore original image.
    const image = imageRef.current;
    if (!image) {
        return;
    }
    image.src = originalUrl;
};

export default MessageAttachment;
